/**
 * extractMessagePayload
 *
 * Normaliza uma mensagem do chat (WA Business OU WA Personal/Evolution) num
 * shape canônico para envio ao backend `/api/ai/generate` e `/api/ai/custom`.
 *
 * Shape canônico:
 *   {
 *     role: 'customer' | 'operator',
 *     type: 'text' | 'image' | 'audio' | 'video' | 'document',
 *     text: string,                  // texto OU caption da mídia
 *     sender_name: string | null,
 *     sender_phone: string | null,
 *     timestamp: number,             // sempre em ms
 *     media: { url, mime_type, file_name, size } | null
 *   }
 *
 * Retorna `null` para tipos não suportados (sticker, vCard, localização).
 */
import { isOpaqueChannel } from '../../../utils/channels';

const PERSONAL_MEDIA_TYPES = [
  ['imageMessage', 'image'],
  ['audioMessage', 'audio'],
  ['videoMessage', 'video'],
  ['documentMessage', 'document'],
];

const MIME_DEFAULT_BY_TYPE = {
  image: 'image/jpeg',
  audio: 'audio/ogg',
  video: 'video/mp4',
  document: 'application/octet-stream',
};

function normalizeTimestamp(msg) {
  // WA Business: ISO string em msg.timestamp ou ms numérico
  const businessTs = msg.timestamp;
  if (businessTs) {
    if (typeof businessTs === 'number') {
      // < 10^12 = segundos (Evolution às vezes manda assim mesmo aqui)
      return businessTs < 1e12 ? businessTs * 1000 : businessTs;
    }
    const parsed = Date.parse(businessTs);
    if (!Number.isNaN(parsed)) return parsed;
  }
  // WA Personal: messageTimestamp em segundos (Unix)
  const personalTs = msg.messageTimestamp;
  if (typeof personalTs === 'number') {
    return personalTs < 1e12 ? personalTs * 1000 : personalTs;
  }
  if (msg.created_at) {
    const parsed = Date.parse(msg.created_at);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
}

function deriveFileName(prefix, mimeType) {
  const ext = mimeType ? mimeType.split('/').pop()?.split(';')[0] : 'bin';
  return `${prefix}_${Date.now()}.${ext || 'bin'}`;
}

function extractContactPhone(chat) {
  if (!chat) return null;
  if (chat.contact_phone) return String(chat.contact_phone);
  if (chat._phoneNumber) return String(chat._phoneNumber);
  if (chat.remoteJid && typeof chat.remoteJid === 'string') {
    return chat.remoteJid.split('@')[0];
  }
  return null;
}

function extractContactName(chat) {
  if (!chat) return '';
  return (
    chat.contact_name ||
    chat.pushName ||
    chat.name ||
    chat.contactName ||
    ''
  );
}

// Instagram/Messenger: media_type carrega o TIPO ('image'|'audio'|'video'|
// 'file'|'share'|'ig_reel'...), não o mime. Só os 4 canônicos entram no
// payload; 'file' vira 'document', o resto (share/story) não é reenviável ao
// agente. O Messenger usa o MESMO shape de doc do IG.
const INSTAGRAM_TYPE_MAP = {
  image: 'image',
  audio: 'audio',
  video: 'video',
  file: 'document',
};

export function extractMessagePayload(msg, { channel, chat, operatorName } = {}) {
  if (!msg) return null;

  // Shape do doc: so o WhatsApp QR persiste o formato canonico da Evolution
  // (`key.fromMe`); todos os outros canais gravam `from_me` no doc.
  const isAgent = channel === 'wa_personal' ? !!msg.key?.fromMe : !!msg.from_me;
  const role = isAgent ? 'operator' : 'customer';
  const sender_name = isAgent
    ? (operatorName || 'Operador')
    : extractContactName(chat);
  const sender_phone = isAgent ? null : extractContactPhone(chat);
  const timestamp = normalizeTimestamp(msg);

  if (isOpaqueChannel(channel)) {
    // Shape do doc dos canais de chave opaca (IG/Messenger): type ('text' ou o tipo da mídia), text,
    // media_url, media_type (tipo, não mime), deleted (soft-delete do
    // cliente). O Messenger ainda traz media_filename para anexos 'file'.
    if (msg.deleted) return null;
    if (msg.media_url) {
      const type = INSTAGRAM_TYPE_MAP[msg.media_type || msg.type];
      if (!type) return null;
      const mime_type = MIME_DEFAULT_BY_TYPE[type];
      return {
        role,
        type,
        text: (msg.text || '').trim(),
        sender_name,
        sender_phone,
        timestamp,
        media: {
          url: msg.media_url,
          mime_type,
          file_name: msg.media_filename || deriveFileName(type, mime_type),
          size: 0,
        },
      };
    }
    const text = (msg.text || '').trim();
    if (!text) return null;
    return { role, type: 'text', text, sender_name, sender_phone, timestamp, media: null };
  }

  if (channel === 'wa_business') {
    if (msg.type === 'text') {
      const text = (msg.text || '').trim();
      if (!text) return null;
      return { role, type: 'text', text, sender_name, sender_phone, timestamp, media: null };
    }
    if (msg.media_url) {
      const type = msg.type || 'document';
      const mime_type = msg.media_type || MIME_DEFAULT_BY_TYPE[type];
      return {
        role,
        type,
        text: (msg.caption || '').trim(),
        sender_name,
        sender_phone,
        timestamp,
        media: {
          url: msg.media_url,
          mime_type,
          file_name: msg.media_filename || deriveFileName(type, mime_type),
          size: msg.media_size || 0,
        },
      };
    }
    return null;
  }

  // wa_personal (Evolution shape) — fallback default
  const m = msg.message || {};
  const textContent = m.conversation || m.extendedTextMessage?.text;
  if (textContent) {
    return {
      role,
      type: 'text',
      text: textContent,
      sender_name,
      sender_phone,
      timestamp,
      media: null,
    };
  }
  for (const [key, type] of PERSONAL_MEDIA_TYPES) {
    const sub = m[key];
    if (sub?.url) {
      const mime_type = sub.mimetype || MIME_DEFAULT_BY_TYPE[type];
      return {
        role,
        type,
        text: (sub.caption || '').trim(),
        sender_name,
        sender_phone,
        timestamp,
        media: {
          url: sub.url,
          mime_type,
          file_name: sub.fileName || deriveFileName(type, mime_type),
          size: sub.fileLength || 0,
        },
      };
    }
  }
  return null;
}

/**
 * Mapeia uma lista de mensagens em payloads canônicos, descarta nulos e
 * ordena cronologicamente — uso recomendado nos handlers de Gerar Resposta.
 */
export function extractMessagesPayload(messages, opts) {
  return (messages || [])
    .map((msg) => extractMessagePayload(msg, opts))
    .filter(Boolean)
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
}

export default extractMessagePayload;
